import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Upload, X, Save, ChevronsUpDown, Check, Loader2, Image, QrCode } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { QRCodeSVG } from "qrcode.react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPDCreateOrder = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Product specifications for dropdown
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [openProductDropdown, setOpenProductDropdown] = useState(false);
  
  // Toggle for enabling/disabling image upload
  const [enableImageUpload, setEnableImageUpload] = useState(false);
  
  // Auto-retrieved image from Prod & Material Info
  const [productImageUrl, setProductImageUrl] = useState(null);
  
  const [formData, setFormData] = useState({
    item_name: "",
    product_id: "",
    company_name: "",
    purchase_order_number: "",
    total_quantity: "",
    status: "Available",
    scheduled_datetime: "",
    note: "",
    image_drive_url: null,
    image_qr_data: null
  });

  // Fetch products on mount
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/product-specifications`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data || []);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to load product list");
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductSelect = (product) => {
    // Get the primary image URL from the product specification
    const primaryImageUrl = product.primary_image_url || product.image_link || (product.image_links && product.image_links.length > 0 ? product.image_links[0].url : null);
    
    setFormData(prev => ({
      ...prev,
      item_name: product.item_name,
      product_id: product.product_id,
      company_name: product.company_name || "",
      // Auto-populate image from product specification if available
      image_drive_url: primaryImageUrl || prev.image_drive_url,
      image_qr_data: primaryImageUrl || prev.image_qr_data
    }));
    
    // Store the product image URL separately for display
    setProductImageUrl(primaryImageUrl);
    
    if (primaryImageUrl) {
      toast.success("QR Code auto-retrieved from Prod & Material Info");
    }
    
    setOpenProductDropdown(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploadingImage(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        // Upload to Google Drive via backend
        const response = await fetch(`${API_URL}/api/spd/upload-image`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            image_base64: base64Data,
            filename: file.name
          })
        });

        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            image_drive_url: data.drive_url,
            image_qr_data: data.drive_url
          }));
          toast.success("Image uploaded to Google Drive");
        } else {
          const error = await response.json();
          toast.error(error.detail || "Failed to upload image");
        }
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      image_drive_url: null,
      image_qr_data: null
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.item_name.trim()) {
      toast.error("Please select an Item");
      return;
    }
    if (!formData.total_quantity) {
      toast.error("Total Quantity is required");
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        item_name: formData.item_name.trim(),
        product_id: formData.product_id,
        company_name: formData.company_name,
        purchase_order_number: formData.purchase_order_number.trim() || null,
        total_quantity: parseInt(formData.total_quantity),
        status: formData.status,
        scheduled_datetime: formData.scheduled_datetime || null,
        note: formData.note.trim() || null,
        image_drive_url: formData.image_drive_url
      };

      const response = await fetch(`${API_URL}/api/spd/orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        toast.success("Order created successfully!");
        navigate("/spd/orders");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create order");
      }
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Failed to create order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="bg-black/80 border-2 border-yellow-500/40 shadow-[0_0_30px_rgba(251,191,36,0.15)]">
        <CardHeader className="border-b border-yellow-500/30">
          <CardTitle className="text-2xl font-black text-yellow-400 uppercase tracking-wider">
            Create Received Purchase Order
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Row 1: Item Name (Searchable Dropdown) & Company Name (Auto-populated) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">
                  Item Name <span className="text-red-400">*</span>
                </Label>
                <Popover open={openProductDropdown} onOpenChange={setOpenProductDropdown}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openProductDropdown}
                      className="w-full justify-between h-12 bg-gray-900/80 border-gray-700 text-white hover:bg-gray-800"
                      data-testid="spd-item-name-dropdown"
                    >
                      {formData.item_name || "Select Item..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0 bg-gray-900 border-gray-700">
                    <Command className="bg-transparent">
                      <CommandInput 
                        placeholder="Search items..." 
                        className="text-white"
                      />
                      <CommandList>
                        <CommandEmpty className="text-gray-400 py-6 text-center">
                          {loadingProducts ? "Loading..." : "No items found."}
                        </CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-y-auto">
                          {products.map((product) => (
                            <CommandItem
                              key={product.id}
                              value={product.item_name}
                              onSelect={() => handleProductSelect(product)}
                              className="text-white hover:bg-gray-800 cursor-pointer"
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.item_name === product.item_name ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span>{product.item_name}</span>
                                <span className="text-xs text-gray-400">
                                  {product.product_id} • {product.company_name}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {formData.product_id && (
                  <p className="text-xs text-gray-400">Product ID: {formData.product_id}</p>
                )}
              </div>
              
              {/* Company Name - Auto-populated */}
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">
                  Company Name
                </Label>
                <Input
                  data-testid="spd-company-name-input"
                  value={formData.company_name}
                  readOnly
                  placeholder="Auto-populated from selected item"
                  className="bg-gray-900/80 border-gray-700 text-white placeholder:text-gray-500 h-12 cursor-not-allowed opacity-80"
                />
              </div>
            </div>

            {/* Row 2: Purchase Order Number & Total Quantity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">
                  Purchase Order Number
                </Label>
                <Input
                  data-testid="spd-po-number-input"
                  value={formData.purchase_order_number}
                  onChange={(e) => handleInputChange("purchase_order_number", e.target.value)}
                  placeholder="Enter PO Number"
                  className="bg-gray-900/80 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 h-12"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">
                  Total Quantity <span className="text-red-400">*</span>
                </Label>
                <Input
                  data-testid="spd-total-quantity-input"
                  type="number"
                  value={formData.total_quantity}
                  onChange={(e) => handleInputChange("total_quantity", e.target.value)}
                  placeholder="Total Quantity *"
                  className="bg-gray-900/80 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 h-12"
                  min="1"
                  required
                />
              </div>
            </div>

            {/* Row 3: Status & Date/Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleInputChange("status", value)}
                >
                  <SelectTrigger 
                    data-testid="spd-status-select"
                    className="bg-gray-900/80 border-gray-700 text-white h-12"
                  >
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="Available" className="text-blue-400 hover:bg-gray-800">Available</SelectItem>
                    <SelectItem value="In Progress" className="text-orange-400 hover:bg-gray-800">In Progress</SelectItem>
                    <SelectItem value="Completed" className="text-emerald-400 hover:bg-gray-800">Completed</SelectItem>
                    <SelectItem value="Rejected" className="text-red-400 hover:bg-gray-800">Rejected</SelectItem>
                    <SelectItem value="Revoked" className="text-gray-400 hover:bg-gray-800">Revoked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">Delivery Due Date</Label>
                <Input
                  data-testid="spd-datetime-input"
                  type="datetime-local"
                  value={formData.scheduled_datetime}
                  onChange={(e) => handleInputChange("scheduled_datetime", e.target.value)}
                  className="bg-gray-900/80 border-gray-700 text-white h-12 [color-scheme:dark]"
                />
              </div>
            </div>

            {/* Note Section */}
            <div className="space-y-2">
              <Label className="text-yellow-400 font-semibold text-lg">
                Note (Optional - Will be prominently displayed)
              </Label>
              <Textarea
                data-testid="spd-note-input"
                value={formData.note}
                onChange={(e) => handleInputChange("note", e.target.value)}
                placeholder="Add any important note or instructions..."
                className="bg-gray-900/80 border-gray-700 text-white placeholder:text-gray-500 focus:border-yellow-500 min-h-[120px] resize-y"
              />
            </div>

            {/* Image Upload Section with QR Code */}
            <div className="space-y-4">
              {/* Auto-retrieved Image/QR from Prod & Material Info */}
              {productImageUrl && (
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-2 border-green-500/40 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <QrCode className="h-5 w-5 text-green-400" />
                    <span className="text-green-400 font-semibold">QR Code Auto-Retrieved from Prod & Material Info</span>
                  </div>
                  <div className="flex items-start gap-6">
                    {/* QR Code */}
                    <div className="flex-shrink-0 bg-white p-3 rounded-lg">
                      <QRCodeSVG 
                        value={productImageUrl} 
                        size={100}
                        level="M"
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1">
                      <p className="text-gray-300 text-sm mb-2">
                        This QR code links to the primary image from the product specification.
                      </p>
                      <a 
                        href={productImageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-sm underline break-all"
                      >
                        View Primary Image
                      </a>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Toggle to Enable/Disable Manual Image Upload */}
              <div className="flex items-center justify-between p-4 bg-gray-900/50 border border-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <Image className="h-5 w-5 text-yellow-400" />
                  <div>
                    <Label className="text-yellow-400 font-semibold">
                      Upload Custom Image
                    </Label>
                    <p className="text-gray-500 text-sm">
                      {productImageUrl 
                        ? "Enable to upload a different image (will override auto-retrieved)" 
                        : "Enable to upload an image for this order"}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={enableImageUpload}
                  onCheckedChange={(checked) => {
                    setEnableImageUpload(checked);
                    if (!checked && productImageUrl) {
                      // Reset to product image when toggle is off
                      setFormData(prev => ({
                        ...prev,
                        image_drive_url: productImageUrl,
                        image_qr_data: productImageUrl
                      }));
                    }
                  }}
                  data-testid="spd-enable-image-upload-toggle"
                />
              </div>
              
              {/* Manual Image Upload Section (only shown when toggle is on) */}
              {enableImageUpload && (
                <div className="space-y-2">
                  <Label className="text-yellow-400 font-semibold text-lg">
                    Upload Image (Optional)
                  </Label>
              
              {formData.image_drive_url && formData.image_drive_url !== productImageUrl ? (
                <div className="bg-gray-900/50 border-2 border-yellow-500/40 rounded-lg p-6">
                  <div className="flex items-start gap-6">
                    {/* QR Code */}
                    <div className="flex-shrink-0 bg-white p-3 rounded-lg">
                      <QRCodeSVG 
                        value={formData.image_drive_url} 
                        size={120}
                        level="M"
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1">
                      <p className="text-green-400 font-semibold mb-2">
                        ✓ Custom image uploaded to Google Drive
                      </p>
                      <p className="text-gray-400 text-sm mb-3">
                        Scan QR code to view the image
                      </p>
                      <a 
                        href={formData.image_drive_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 text-sm underline break-all"
                      >
                        {formData.image_drive_url}
                      </a>
                      <div className="mt-4">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={removeImage}
                          className="border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white"
                          data-testid="spd-remove-image-btn"
                        >
                          <X className="h-4 w-4 mr-2" />
                          Remove Image
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    uploadingImage 
                      ? 'border-yellow-500/50 bg-yellow-500/10' 
                      : 'border-gray-700 hover:border-yellow-500/50 cursor-pointer'
                  }`}
                  onClick={() => !uploadingImage && fileInputRef.current?.click()}
                >
                  {uploadingImage ? (
                    <>
                      <Loader2 className="h-10 w-10 text-yellow-400 mx-auto mb-2 animate-spin" />
                      <p className="text-yellow-400">Uploading to Google Drive...</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-gray-500 mx-auto mb-2" />
                      <p className="text-gray-400">Click to upload image</p>
                      <p className="text-gray-500 text-sm mt-1">Image will be saved to Google Drive & QR code generated</p>
                      <p className="text-gray-500 text-sm">Max file size: 5MB</p>
                    </>
                  )}
                </div>
              )}
                </div>
              )}
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="spd-image-upload-input"
                disabled={uploadingImage}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4 pt-4 border-t border-yellow-500/30">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/spd/dashboard")}
                className="border-gray-600 text-gray-400 hover:bg-gray-800 font-bold uppercase tracking-wider"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || uploadingImage}
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold uppercase tracking-wider shadow-lg px-8"
                data-testid="spd-submit-order-btn"
              >
                {loading ? (
                  "Creating..."
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Order
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SPDCreateOrder;
